import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { I18n } from '../../_ui-core_/base/package/services/package/i18n/i18n.de';
import App from '../../app';
import { LoginStatus } from '../../app/package/base/service/login/login-domain';
import { TrackableEvent, TrackableEventAction } from '../../app/package/base/service/tracking/tracking-service';
import style from './styles.css?inline';
import '../../_ui-core_/components/icons/ui-exit-icon';
import '../../_ui-core_/components/ui-icons/ui-clock-icon';
import '../../page-templates/base-page/base-page';

export const LogOutReason = {
  UNAUTHENTICATED: 'unauthenticated',
  DEFAULT: 'default',
  INACTIVITY: 'inactivity',
  OASIS: 'oasis',
  PANIC: 'panic',
} as const;

export type LogOutReason = (typeof LogOutReason)[keyof typeof LogOutReason];

const CName = 'logout-page';

@customElement(CName)
export class LogoutPage extends LitElement {
  static readonly styles = unsafeCSS(style);
  @property() reason: LogOutReason = LogOutReason.DEFAULT;

  async connectedCallback() {
    super.connectedCallback();

    // Navigate to home if user is logged in. This page will be shown after login again if user selects the login button of this page.
    const isLoggedIn = (await App.instance.login.getFinalLoginStatus()) === LoginStatus.LOGGED_IN;
    if (!this.isConnected) return;
    if (isLoggedIn) {
      App.instance.router.navigateToHome();
      return;
    }

    this._trackEvent(TrackableEventAction.SHOWN);
  }

  render() {
    return html`<base-page>${this._renderMain()}</base-page>`;
  }

  private _renderMain() {
    const config = this._getReasonConfig();

    return html`<main class="main">
      <div class="content">
        <div class="icon">
          ${this.reason === LogOutReason.INACTIVITY ? html`<ui-clock-icon></ui-clock-icon>` : html`<ui-exit-icon></ui-exit-icon>`}
        </div>
        <h1 class="title">${window.$app.$t.get(config.title)}</h1>
        ${config.message ? html`<div class="message">${window.$app.$t.get(config.message)}</div>` : nothing}
        <div class="buttons">
          <ui-btn
            variant="secondary"
            .isBlock=${true}
            @click=${this._handlePrimaryClick}
          >
            ${window.$app.$t.get(I18n.base.login)}
          </ui-btn>
          <ui-btn
            variant="secondary-outlined"
            .isBlock=${true}
            @click=${this._handleSecondaryClick}
          >
            ${window.$app.$t.get(config.secondaryCta)}
          </ui-btn>
        </div>
      </div>
    </main>`;
  }

  private _getReasonConfig(): {
    title: string;
    message: string;
    secondaryCta: string;
    secondaryAction: 'home' | 'support';
  } {
    switch (this.reason) {
      case LogOutReason.INACTIVITY:
        return {
          title: I18n.base.logOutReason.inactivity.title,
          message: I18n.base.logOutReason.inactivity.message,
          secondaryCta: I18n.base.logOutReason.inactivity.secondaryCta,
          secondaryAction: 'home',
        };
      case LogOutReason.PANIC:
        return {
          title: I18n.base.logOutReason.panic.title,
          message: I18n.base.logOutReason.panic.message,
          secondaryCta: I18n.base.logOutReason.panic.secondaryCta,
          secondaryAction: 'support',
        };
      case LogOutReason.OASIS:
        return {
          title: I18n.base.logOutReason.oasis.title,
          message: I18n.base.logOutReason.oasis.message,
          secondaryCta: I18n.base.logOutReason.oasis.secondaryCta,
          secondaryAction: 'support',
        };
      case LogOutReason.UNAUTHENTICATED:
        return {
          title: I18n.base.logOutReason.unauthenticated.title,
          message: I18n.base.logOutReason.unauthenticated.message,
          secondaryCta: I18n.base.backToHome,
          secondaryAction: 'home',
        };
      default:
        return {
          title: I18n.base.logOutReason.generic.title,
          message: I18n.base.logOutReason.generic.message,
          secondaryCta: I18n.base.logOutReason.generic.secondaryCta,
          secondaryAction: 'support',
        };
    }
  }

  private _handlePrimaryClick() {
    this._trackEvent(TrackableEventAction.LOGIN);
    App.instance.product.gotoLogin(CName);
  }

  private _handleSecondaryClick() {
    const config = this._getReasonConfig();
    this._trackEvent(TrackableEventAction.LOBBY);
    if (config.secondaryAction === 'support') {
      App.instance.product.gotoHelpCenterOrContactUs();
    } else {
      App.instance.router.navigateToHome();
    }
  }

  private _trackEvent(eventAction: TrackableEventAction) {
    const event = this.reason === LogOutReason.DEFAULT ? TrackableEvent.LOGOUT_PAGE : TrackableEvent.SESS_LOGGED_OUT_PG;
    window.$app.track.logoutPage(event, eventAction);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: LogoutPage;
  }
}
